import { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { cardToDeleteStore } from "features/CardToDelete";
import { openedCardStore } from "features/OpenedCard";
import { useParcelCardActions } from "features/ParcelCardActions/model/hooks/useParcelCardActions";
import { EventList } from "features/ParcelOpenedCardTabs";
import { useParcelUrlQueryHandler } from "features/QueryParamsManager";
import { useParcelSearch } from "features/Search/model/hooks/parcelSearch.hook";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { Card, CardBase } from "entities/Card";
import { useOpenedCardSearchQuery } from "entities/ParcelSearchCard";
import { isParcelSearchShipmentModel } from "entities/ParcelSearchCard";
import { searchFilterStore } from "entities/SearchFilter";
import { subscriptionStore } from "entities/Subscription";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import { ButtonIcon } from "shared/ui/Button";
import { Preloader } from "shared/ui/Preloader";
import { Tabs } from "shared/ui/Tabs";
import { Tooltip } from "shared/ui/Tooltip";
import s from "./OpenedCardView.module.scss";

import { TabIdList } from "./OpenedCardView.constants";

type ParcelOpenedCardLayerProps = {
  className?: string;
};

export const ParcelOpenedCardView: RFC<ParcelOpenedCardLayerProps> = ({ className }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);
  const isCardSearchVisible = useAppSelector(appStore.selectors.isCardSearchVisible);
  const { isCurrentViewOpenedCard, currentView } = usePanelViews();

  const parcelOpenedCard = useMemo(() => {
    if (isParcelSearchShipmentModel(openedCard)) return openedCard;
    return null;
  }, [openedCard]);

  const requestedFromUrl = useAppSelector(appStore.selectors.requestedFromUrl);
  const { deleteParcelCardHandler } = useParcelCardActions({
    setCardToDeleteAction: (card) => ({
      type: cardToDeleteStore.actions.setCardToDeleteAction.type,
      payload: card,
    }),
    resetCardToDeleteAction: () => ({
      type: cardToDeleteStore.actions.resetCardToDeleteAction.type,
    }),
  });

  const parcelSearchRequestMeta = useParcelSearch({ isAuthenticated });

  const [tooltipLinkName, setTooltipLinkName] = useState("Delete");
  useParcelUrlQueryHandler({ openedCard: parcelOpenedCard });

  const openedCardSearchRequestMeta = useOpenedCardSearchQuery({
    isAuthenticated,
    setSubscription: (subscription) => ({
      type: subscriptionStore.actions.setSubscription.type,
      payload: subscription,
    }),
    setActivePaymentModal: (activePaymentModal) => ({
      type: subscriptionStore.actions.setActivePaymentModal.type,
      payload: activePaymentModal,
    }),
    setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
      type: appStore.actions.setAppIntegrationRequestLimitReached.type,
      payload: appIntegrationRequestLimitReached,
    }),
    setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
      type: appStore.actions.setWebAppRequestLimitReached.type,
      payload: webAppRequestLimitReached,
    }),
  });

  const { searchResponse: cardData } = openedCardSearchRequestMeta;

  useEffect(() => {
    if (!requestedFromUrl) return;

    if (isParcelSearchShipmentModel(parcelOpenedCard) && parcelOpenedCard.rawData) {
      openedCardSearchRequestMeta.setSearchCache(parcelOpenedCard.rawData);
    }
  }, [parcelOpenedCard?.rawData]);

  useEffect(() => {
    if (
      !isCurrentViewOpenedCard ||
      openedCardSearchRequestMeta.isSearchLoading ||
      openedCardSearchRequestMeta.isSearchFetching
    ) {
      return;
    }

    if (isParcelSearchShipmentModel(parcelOpenedCard) && parcelOpenedCard.rawData) {
      openedCardSearchRequestMeta.setSearchCache(parcelOpenedCard.rawData);
      return;
    }

    // if (isRailSavedShipmentModel(railOpenedCard)) {
    //   const reqParams = createRailSearchQueryParams({
    //     requestType: "openedCard",
    //     override: { number: railOpenedCard.reference },
    //   });
    //   if (!reqParams) return;
    //   openedCardSearchRequestMeta.searchRequest(reqParams);
    // }
  }, [currentView, parcelOpenedCard?.rawData]);

  // useEffect(() => {
  //   if (openedCardTrackingRequestMeta.trackingResponse?.rawDataParser.requestLimitReached) {
  //     clearViewHistory();
  //     showMainView();
  //     dispatch(setOpenedCardAction(null));
  //   }
  // }, [openedCardTrackingRequestMeta.trackingResponse?.rawData]);

  useEffect(
    () => () => {
      openedCardSearchRequestMeta.resetSearchQuery();
      dispatch(openedCardStore.actions.setOpenedCardAction(null));
      openedCardSearchRequestMeta.cancelSearchRequest();
    },
    []
  );

  // const initialTabId = useMemo<TabIdList>(() => {
  //   const referenceType = cardData?.referenceType;
  //   if (!referenceType) {
  //     return TabIdList.routes;
  //   }
  //   return defaultActiveTabMap[referenceType.toUpperCase() as TrackingReferenceTypes] ?? TabIdList.routes;
  // }, [cardData?.referenceType]);

  // const changeShippingLineRequest: ChangeShippingLineRequestFunc = (sealine) => {
  //   const reqParams = createTrackingQueryParams(openedCard, { requestType: 'shippingLine', override: { sealine } });
  //   if (!reqParams) return;

  //   changeShippingLineSearchMeta.trackingRequest(reqParams, {
  //     onSuccess: (response) => {
  //       if (response.rawDataParser.isSuccess) {
  //         dispatch(setOpenedCardAction(response));
  //         dispatch(updateTrackingCardAction(response));
  //       }
  //     },
  //   });
  // };

  // const onUpdateHandler = () => {
  //   return isSavedAirModel(openedCard) ? updateCard(openedCard) : undefined;
  // };

  // const shouldUpdateCardHandler = (): boolean => {
  //   return isSavedAirModel(openedCard) ? shouldUpdateCard() : false;
  // };

  const onDeleteHandler = () => {
    if (!parcelOpenedCard) return;
    deleteParcelCardHandler(parcelOpenedCard);
  };

  const copyUrlHandler = () => {
    const currentURL = window.location.href;
    navigator.clipboard.writeText(currentURL).then(() => {
      setTooltipLinkName(t("Copied"));
    });
  };

  // const polCoordinate = useMemo<HiddentInputCoordinate | null>(() => {
  //   const pol = cardData?.rawDataParser.getLoadingCoordinate();
  //   if (!pol) return null;
  //   return [pol.lat, pol.lng];
  // }, [cardData?.rawData]);

  // const podCoordinate = useMemo<HiddentInputCoordinate | null>(() => {
  //   const pod = cardData?.rawDataParser.getDepartureCoordinate();
  //   if (!pod) return null;
  //   return [pod.lat, pod.lng];
  // }, [cardData?.rawData]);

  if (
    openedCardSearchRequestMeta.isSearchLoading ||
    openedCardSearchRequestMeta.isSearchFetching ||
    parcelSearchRequestMeta.isParcelSearchLoading
  ) {
    return <Preloader className={cn(s.loader, className)} />;
  }

  // // TODO: clarify this logic
  // // if (
  // //   !searchResponse?.rawDataParser.isSuccess ||
  // //   isSearchError ||
  // //   targetTrackingCardMeta.isTrackingError
  // //   // !targetTrackingCardMeta.trackingResponse?.rawDataParser.isSuccess
  // // ) {
  // //   return (
  // //     <div>
  // //       We sincerely apologize for any inconvenience caused. Regrettably, we have encountered an error pertaining to
  // //       this specific shipment. To rectify the situation, we kindly request you to navigate back using the designated
  // //       'back' button and attempt to initiate a new search or explore alternative shipments.
  // //     </div>
  // //   );
  // // }

  if (!parcelOpenedCard) {
    return <div>{t("No data found!")}</div>;
  }

  return (
    <>
      <div className={s.cardWrapper}>
        <Card
          shipmentType={shipmentType}
          className={s.openedCardShipmentCard}
          ata={cardData?.ata}
          atd={cardData?.atd}
          eta={cardData?.eta}
          etd={cardData?.etd}
          scac={cardData?.carrierCode}
          reference={cardData?.reference}
          carrierName={cardData?.carrierName ?? ""}
          status={cardData?.status ?? null}
          directionFrom={cardData?.fromDirection}
          directionTo={cardData?.toDirection}
          progressLine={{ status: "location-data", percent: cardData?.progress ?? 0, rotation: "horizontal" }}
          onDelete={onDeleteHandler}
        />
      </div>

      <div className={cn(s.cardsWrapper, s.cardWrapper)}>
        <CardBase
          className={cn(s.openedCard, className)}
          // onUpdate={onUpdateHandler}
          // canUpdate={shouldUpdateCardHandler()}
          // isUpdating={isResolvingUpdatePossibility}
        >
          <CardBase.Row className={s.tabsRow}>
            <Tabs className={s.tabs} initialTabId={TabIdList.route}>
              <Tabs.Row className={s.tabsControls}>
                <Tabs.Group>
                  <Tabs.Control dataFor={TabIdList.route}>
                    <Tabs.ControlLabel>{t("History")}</Tabs.ControlLabel>
                  </Tabs.Control>
                  {/* TODO: uncomment route tab */}
                  {/* <Tabs.Control dataFor={TabIdList.details}>
                    <Tabs.ControlLabel>{t("Route")}</Tabs.ControlLabel>
                  </Tabs.Control> */}
                </Tabs.Group>
              </Tabs.Row>
              <Tabs.Body className={s.tabsBody}>
                <Tabs.Panel data-tab={TabIdList.route} className={s["panel"]}>
                  <EventList data={cardData} />
                </Tabs.Panel>
                {/* TODO: uncomment route tab */}
                {/* <Tabs.Panel data-tab={TabIdList.details} className={cn(s["panel"], s["vessels-panel"])}>
                  <LineList data={cardData} />
                </Tabs.Panel> */}
              </Tabs.Body>
            </Tabs>
          </CardBase.Row>

          <CardBase.Row className={cn(s.divider, s.bottomActions)}>
            <div className={s.actionsContainer}>
              {isCardSearchVisible && (
                <Tooltip
                  triggerElement={
                    <ButtonIcon
                      // disabled={isRailCardDeleting}
                      icon="trash"
                      className={s.delete}
                      onClick={onDeleteHandler}
                      data-test-id={`openedCard-action-delete-${openedCard?.reference}`}
                    />
                  }
                  offsetY={5}
                >
                  <p className={s.tooltipButtonsText}>{t("Delete")}</p>
                </Tooltip>
              )}

              <Tooltip
                triggerElement={
                  <ButtonIcon
                    icon="link"
                    onClick={copyUrlHandler}
                    className={s.linkButton}
                    onMouseEnter={() => setTooltipLinkName(t("Copy"))}
                    data-test-id={`openedCard-action-copy-${openedCard?.reference}`}
                  />
                }
                offsetY={5}
              >
                <p className={s.tooltipButtonsText}>{tooltipLinkName}</p>
              </Tooltip>
            </div>
          </CardBase.Row>
        </CardBase>
      </div>
    </>
  );
};

ParcelOpenedCardView.displayName = ParcelOpenedCardView.name;
